import { BrandConfig } from '../config/brand';

/**
 * Fetch BRAND_CONFIG from a backend API
 * 
 * Supports querying by tenant/brand ID for multi-tenant applications.
 * Falls back to undefined if fetch fails - caller should use embedded defaults.
 * 
 * @param brandId - Optional tenant/brand identifier. If provided, API should return config for that brand.
 * @param apiEndpoint - Optional API endpoint. Falls back to VITE_BRAND_API_URL env var.
 * @returns BrandConfig object or null if fetch fails
 * 
 * API Response Format Expected:
 * {
 *   success: boolean;
 *   data: BrandConfig;
 *   error?: string;
 * }
 * 
 * API Endpoint Examples:
 * - GET /api/brand   (returns default brand config)
 * - GET /api/brand?brandId=tenant-123   (returns tenant-specific config)
 * - GET /api/brand/tenant-123   (alternative REST path structure)
 */
export async function fetchBrandConfig(
  brandId?: string,
  apiEndpoint?: string
): Promise<BrandConfig | null> {
  try {
    // Determine API endpoint
    const endpoint = apiEndpoint || import.meta.env.VITE_BRAND_API_URL;
    
    if (!endpoint) {
      console.warn(
        'No BRAND_API_URL configured. Set VITE_BRAND_API_URL environment variable to enable dynamic brand config. ' +
        'Using embedded defaults.'
      );
      return null;
    }

    // Build request URL
    let url = endpoint;
    if (brandId) {
      // Support query parameter style: /api/brand?brandId=tenant-123
      url += url.includes('?') ? '&' : '?';
      url += `brandId=${encodeURIComponent(brandId)}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      console.error(`Brand config API returned status ${response.status}: ${response.statusText}`);
      return null;
    }

    const responseData = await response.json();

    // Handle API response format { success, data, error }
    if (!responseData.success) {
      console.error(`Brand config API error: ${responseData.error || 'Unknown error'}`);
      return null;
    }

    if (!responseData.data) {
      console.error('Brand config API returned empty data');
      return null;
    }

    return responseData.data as BrandConfig;
  } catch (error) {
    console.error('Failed to fetch brand config:', error);
    return null;
  }
}

/**
 * Update BRAND_CONFIG via a backend API (for admin dashboard use)
 * 
 * @param brandConfig - Updated configuration to send to server
 * @param apiEndpoint - Optional API endpoint. Falls back to VITE_BRAND_API_URL env var.
 * @returns Success status
 */
export async function updateBrandConfig(
  brandConfig: Partial<BrandConfig>,
  apiEndpoint?: string
): Promise<boolean> {
  try {
    const endpoint = apiEndpoint || import.meta.env.VITE_BRAND_API_URL;

    if (!endpoint) {
      console.error('No BRAND_API_URL configured for updates');
      return false;
    }

    const response = await fetch(endpoint, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(brandConfig),
    });

    if (!response.ok) {
      console.error(`Brand config update returned status ${response.status}`);
      return false;
    }

    const responseData = await response.json();

    if (!responseData.success) {
      console.error(`Brand config update failed: ${responseData.error}`);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Failed to update brand config:', error);
    return false;
  }
}

/**
 * Check if a backend API is available for brand config
 */
export function isBrandApiConfigured(): boolean {
  return !!import.meta.env.VITE_BRAND_API_URL;
}
